import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiX } from "react-icons/fi";
import { ShinyText } from "../Home/Home";
import { NavLink } from "react-router-dom";
import { PiMicrophoneFill } from "react-icons/pi";
import { ReactSVG } from "react-svg";
import translationIcon from "../../Asset/Real-Time Translation.svg";
import voiceStar from "../../Asset/voiceStar.svg";
import Lottie from "lottie-react";
import iconAnimation from "../../Asset/json/Icon Animation.json";
import startRecordingIcon from "../../Asset/startRecordingIcon.svg";
import { IoIosArrowDown, IoMdSettings } from "react-icons/io";
import fixGrammar from "../../Asset/Fix Grammar.svg";
import professional from "../../Asset/Professional.svg";
import formatText from "../../Asset/Format Text.svg";
import transcribeOnly from "../../Asset/Transcribe Only.svg";
import askGPT from "../../Asset/Ask GPT1.svg";
import rephrase from "../../Asset/Rephrase.svg";
import casual from "../../Asset/Casual.svg";
import organizeThoughts from "../../Asset/Organize Thoughts.svg";
import axios from "axios";
import { toast } from "react-toastify";
import DemoLoader from "../../component/Loader/demoLoader";
import { TbTriangleInvertedFilled } from "react-icons/tb";

/* =========================
   Config
========================= */
const options = [
  { label: "Fix Grammar", icon: fixGrammar, bgColor: "#05280826" },
  { label: "Professional", icon: professional, bgColor: "#7E5C4926" },
  { label: "Format Text", icon: formatText, bgColor: "#D4633F26" },
  { label: "Transcribe Only", icon: transcribeOnly, bgColor: "#3432D726" },
  { label: "Ask GPT", icon: askGPT, bgColor: "#3AB1F624" },
  { label: "Rephrase", icon: rephrase, bgColor: "#7EA3C426" },
  { label: "Casual", icon: casual, bgColor: "#FFBC2926" },
  { label: "Organize Thoughts", icon: organizeThoughts, bgColor: "#FF9BBA26" },
];

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const TRANSLATE_URL = `${BACKEND}api/translate`;
const APPLY_PROMPT_URL = `${BACKEND}api/applyPrompt`;

// Convert the backend HTTP URL to a local WebSocket URL (no auth token needed)
const SOCKET_URL = (() => {
  const base = BACKEND.replace(/\/+$/, "");
  if (!base) return "";
  return base.replace(/^http/, "ws") + "/transcribe";
})();

/* =========================
   Audio helpers (WS pipeline)
========================= */
const TARGET_SAMPLE_RATE = 24000;
const PROC_BUFFER_SIZE = 1024;
const HEAD_PAD_MS = 200;
const TAIL_PAD_MS = 200;

const floatToPCM16 = (float32) => {
  const out = new Int16Array(float32.length);
  for (let i = 0; i < float32.length; i++) {
    let s = Math.max(-1, Math.min(1, float32[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
};

const downsampleFloat32 = (buffer, inRate, outRate) => {
  if (outRate === inRate) return buffer;
  const ratio = inRate / outRate;
  const newLen = Math.round(buffer.length / ratio);
  const result = new Float32Array(newLen);
  let o = 0,
    i = 0;
  while (o < newLen) {
    const next = Math.round((o + 1) * ratio);
    let sum = 0,
      count = 0;
    for (let j = Math.round(i); j < next && j < buffer.length; j++) {
      sum += buffer[j];
      count++;
    }
    result[o++] = count ? sum / count : 0;
    i = next;
  }
  return result;
};

const b64FromArrayBuffer = (buf) => {
  const bytes = new Uint8Array(buf);
  let bin = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(bin);
};

/* =========================
   Hook: Realtime transcription via local WebSocket (no auth token required)
========================= */
function useRealtimeTranscription({ wsUrl, onFinalText, onError }) {
  const wsRef = useRef(null);
  const streamRef = useRef(null);
  const audioContextRef = useRef(null);
  const processorRef = useRef(null);
  const muteRef = useRef(null);
  const ctxRateRef = useRef(null);
  const recordingRef = useRef(false);
  const lastTickRef = useRef(0);
  const finalizeAfterCommitRef = useRef(false);

  const ensureSocketOpen = async () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return wsRef.current;

    if (!wsUrl) throw new Error("Realtime websocket URL is not configured.");

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onmessage = (e) => {
      let msg;
      try {
        msg = JSON.parse(e.data);
      } catch {
        return;
      }

      if (
        msg.type === "transcription.completed" ||
        msg.type === "conversation.item.input_audio_transcription.completed"
      ) {
        const text = typeof msg.text === "string" ? msg.text : msg.transcript || "";
        onFinalText?.((text || "").trim());
        try {
          ws.send(JSON.stringify({ type: "input_audio_buffer.clear" }));
        } catch {}
        finalizeAfterCommitRef.current = false;
      }

      if (msg.type === "error" || msg.type === "invalid_request_error") {
        const emsg = (msg?.error?.message || msg?.message || "Realtime error").toString();
        onError?.(emsg);
        try { ws.close(); } catch {}
        wsRef.current = null;
      }
    };

    ws.onerror = () => onError?.("WebSocket connection error.");
    ws.onclose = () => {};

    await new Promise((resolve, reject) => {
      const t = setTimeout(() => reject(new Error("WS open timeout")), 7000);
      ws.onopen = () => { clearTimeout(t); resolve(); };
      ws.onerror = () => { clearTimeout(t); reject(new Error("WebSocket connection error.")); };
    });

    return ws;
  };

  const sendZeros = async (ms) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const total = Math.round((ms / 1000) * TARGET_SAMPLE_RATE);
    if (total <= 0) return;
    const CHUNK_MS = 50;
    const chunkSamples = Math.round((CHUNK_MS / 1000) * TARGET_SAMPLE_RATE);
    let sent = 0;
    while (sent < total && ws.readyState === WebSocket.OPEN) {
      const n = Math.min(chunkSamples, total - sent);
      const zeros16 = new Int16Array(n);
      ws.send(JSON.stringify({ type: "input_audio_buffer.append", audio: b64FromArrayBuffer(zeros16.buffer) }));
      sent += n;
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 0));
    }
  };

  const waitOneAudioTick = (timeoutMs = 500) =>
    new Promise((resolve) => {
      const start = performance.now();
      const tick = () => {
        const elapsed = performance.now() - lastTickRef.current;
        if (elapsed < 30) return resolve();
        if (performance.now() - start > timeoutMs) return resolve();
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    });

  const waitForWSDrain = async (socket, timeoutMs = 800) => {
    const start = performance.now();
    while (socket && socket.readyState === WebSocket.OPEN && socket.bufferedAmount > 0) {
      if (performance.now() - start > timeoutMs) break;
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 15));
    }
  };

  const start = async () => {
    try {
      onError?.("");
      await ensureSocketOpen();

      const media = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1, sampleRate: TARGET_SAMPLE_RATE },
        video: false,
      });
      streamRef.current = media;

      const ctx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: TARGET_SAMPLE_RATE });
      audioContextRef.current = ctx;
      ctxRateRef.current = ctx.sampleRate;

      if (ctx.state === "suspended") { try { await ctx.resume(); } catch {} }

      const source = ctx.createMediaStreamSource(media);
      const proc = ctx.createScriptProcessor(PROC_BUFFER_SIZE, 1, 1);
      const mute = ctx.createGain();
      mute.gain.value = 0;
      processorRef.current = proc;
      muteRef.current = mute;

      let dropWarmup = 3;
      proc.onaudioprocess = (e) => {
        const ws = wsRef.current;
        lastTickRef.current = performance.now();
        if (!ws || ws.readyState !== WebSocket.OPEN) return;
        if (!recordingRef.current || !ctxRateRef.current) return;
        if (dropWarmup > 0) { dropWarmup--; return; }
        const input = e.inputBuffer.getChannelData(0);
        const f16k = downsampleFloat32(input, ctxRateRef.current, TARGET_SAMPLE_RATE);
        if (!f16k.length) return;
        const pcm16 = floatToPCM16(f16k);
        ws.send(JSON.stringify({ type: "input_audio_buffer.append", audio: b64FromArrayBuffer(pcm16.buffer) }));
      };

      source.connect(proc);
      proc.connect(mute);
      mute.connect(ctx.destination);

      await sendZeros(HEAD_PAD_MS);
      recordingRef.current = true;
    } catch (err) {
      onError?.(err?.message || "Start failed");
      throw err;
    }
  };

  const stop = async () => {
    const ws = wsRef.current;

    if (processorRef.current) {
      processorRef.current.onaudioprocess = null;
      try { processorRef.current.disconnect(); } catch {}
      processorRef.current = null;
    }
    if (muteRef.current) {
      try { muteRef.current.disconnect(); } catch {}
      muteRef.current = null;
    }

    recordingRef.current = false;
    await waitOneAudioTick(500);

    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      try { await audioContextRef.current.close(); } catch {}
      audioContextRef.current = null;
      ctxRateRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }

    if (ws && ws.readyState === WebSocket.OPEN) {
      await sendZeros(TAIL_PAD_MS);
      await waitForWSDrain(ws, 1500);
      finalizeAfterCommitRef.current = true;
      try { ws.send(JSON.stringify({ type: "input_audio_buffer.commit" })); } catch {}
      await waitForWSDrain(ws, 800);
    }
  };

  const teardown = () => {
    recordingRef.current = false;
    try { if (processorRef.current) processorRef.current.onaudioprocess = null; } catch {}
    try { if (audioContextRef.current && audioContextRef.current.state !== "closed") audioContextRef.current.close(); } catch {}
    try { if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop()); } catch {}
    try { if (wsRef.current) wsRef.current.close(); } catch {}
    processorRef.current = null;
    audioContextRef.current = null;
    streamRef.current = null;
    wsRef.current = null;
  };

  useEffect(() => teardown, []);

  return { start, stop, teardown };
}

/* =========================
   Hook: File-based translation via MediaRecorder upload
========================= */
function useFileTranslation({ translateUrl, onText, onError, onLoading }) {
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    onError?.("");
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;

    const rec = new MediaRecorder(stream);
    mediaRecorderRef.current = rec;
    chunksRef.current = [];

    rec.ondataavailable = (e) => {
      chunksRef.current.push(e.data);
    };

    rec.start();
  };

  const stop = async ({ promptType, type }) => {
    const rec = mediaRecorderRef.current;
    const stream = streamRef.current;

    if (!rec || rec.state === "inactive") return;

    const blob = await new Promise((resolve) => {
      rec.onstop = () => {
        const out = new Blob(chunksRef.current, { type: "audio/webm" });
        resolve(out);
      };
      rec.stop();
    });

    if (stream) stream.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    mediaRecorderRef.current = null;

    const formData = new FormData();
    formData.append("audio", blob, "recording.webm");
    formData.append("promptType", promptType || "Transcribe Only");
    formData.append("type", type || "translation");

    onLoading?.(true);
    try {
      const res = await axios.post(translateUrl, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onText?.(res.data?.text || "");
    } catch (err) {
      const message =
        err?.response?.data?.error || "Something went wrong during translation.";
      onError?.(message);
    } finally {
      onLoading?.(false);
    }
  };

  const teardown = () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
    } catch {}
    try {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    } catch {}
    mediaRecorderRef.current = null;
    streamRef.current = null;
    chunksRef.current = [];
  };

  useEffect(() => teardown, []);

  return { start, stop, teardown };
}

/* =========================
   Component
========================= */
const Demo = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(options[3]);
  const [type, setType] = useState("transcription");
  const [recording, setRecording] = useState(false);

  const [transcription, setTranscription] = useState("");
  const [translation, setTranslation] = useState("");
  const [refineText, setRefineText] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const dropdownRef = useRef(null);
  const promptAtStartRef = useRef(options[3]?.label || "Transcribe Only");

  const textOut = transcription || translation;
  const hastext = Boolean(textOut?.trim()?.length || refineText?.trim()?.length);

  const wordCount = useMemo(() => {
    const t = (refineText?.length ? refineText : textOut) || "";
    return t ? t.trim().split(/\s+/).filter(Boolean).length : 0;
  }, [textOut, refineText]);

  const applyPrompt = async ({ content, promptType }) => {
    if (!content?.trim()) {
      toast.error("No text to apply prompt.");
      return;
    }

    // If Transcribe Only, show raw transcript
    if (promptType === "Transcribe Only") {
      setRefineText("");
      setTranscription(content);
      return;
    }

    setLoading(true);
    setErrorMsg("");

    try {
      const res = await axios.post(APPLY_PROMPT_URL, {
        promptType,
        content,
      });

      // show ONLY refined
      setTranscription("");
      setRefineText(res.data?.text || "");
    } catch (err) {
      const message =
        err?.response?.data?.error || "Something went wrong during Apply Prompt.";
      setErrorMsg(message);

      // fallback to raw if refine fails
      setTranscription(content);
      setRefineText("");
    } finally {
      setLoading(false);
    }

    if (hastext) {
      toast.success("Settings applied successfully!");
    }
  };

  // Hooks
  const rt = useRealtimeTranscription({
  wsUrl: SOCKET_URL,
  onFinalText: async (t) => {
    try {
      const lockedPrompt = promptAtStartRef.current || "Transcribe Only";

      if (lockedPrompt !== "Transcribe Only") {
        setTranscription("");
        setRefineText("");
      }

      await applyPrompt({ content: t, promptType: lockedPrompt });
    } finally {
      setFinalizing(false);
    }
  },
  onError: (m) => {
    setErrorMsg(m);
    setFinalizing(false); // ✅ also stop loader on error
  },
});


  const fileTx = useFileTranslation({
    translateUrl: TRANSLATE_URL,
    onText: (t) => setTranslation(t),
    onError: (m) => setErrorMsg(m),
    onLoading: (v) => setLoading(v),
  });

  const resetOutputs = () => {
    setTranscription("");
    setTranslation("");
    setRefineText("");
  };

  const startUnified = async () => {
    try {
      setErrorMsg("");
      resetOutputs();
      promptAtStartRef.current = selected?.label || "Transcribe Only";

      setRecording(true);

      if (type === "transcription") {
        await rt.start();
      } else {
        await fileTx.start();
      }
    } catch (e) {
      setRecording(false);
    }
  };

const stopUnified = async () => {
  try {
    setErrorMsg("");
    setRecording(false);

    setFinalizing(true);

    if (type === "transcription") {
      await rt.stop();
    } else {
      await fileTx.stop({ promptType: selected?.label || "Transcribe Only", type });
      setFinalizing(false);
    }
  } catch (e) {
    setFinalizing(false);
  }
};

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);


  // if user switches type while recording, stop cleanly
  useEffect(() => {
    if (!recording) return;
    stopUnified();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  return (
    <div className="bg-[#F1FAFF] flex flex-col lg:gap-5 md:gap-4 gap-3 md:p-8 p-4 min-h-screen overflow-hidden">
      <div className="flex justify-end">
        <NavLink to={"/"}>
          <FiX className="md:size-8 size-6" />
        </NavLink>
      </div>

      <span className="bg-white w-max text-center mx-auto md:text-sm text-[10px] p-3 px-5 rounded-full">
        <ShinyText text="ZeroTyping AI Transcription" />
      </span>

      <div className="w-max mx-auto md:text-[3.373rem] text-[2.1875rem] font-bold font-jakarta">
        <ShinyText text="Speak " />
        <span>Your Mind</span>
      </div>

      <div className="xl:text-[1.687rem] lg:text-xl md:text-lg font-inter demo text-wrap text-center mx-auto text-[#7E7E7E]">
        Flawless voice typing! Simply speak, and let our AI handle the rest.
      </div>

      <div className="flex justify-center w-max mx-auto">
        <div className="bg-white md:p-2 p-1.5 md:rounded-[20px] rounded-xl border border-[#3AB1F64D] flex md:gap-2 gap-1 w-full md:text-sm text-xs font-inter">
          <button
            onClick={() => setType("transcription")}
            className={`flex-1 md:py-2.5 py-1.5 md:px-10 px-4 md:rounded-xl rounded-md flex items-center md:gap-2 gap-1.5 transition ${
              type === "transcription"
                ? "bg-gradient-to-r from-[#3AB1F6] to-[#407BFF] text-white font-semibold"
                : "text-[#0A0A0A]"
            }`}
          >
            <PiMicrophoneFill />
            Transcription
          </button>

          <button
            onClick={() => setType("translation")}
            className={`flex-1 md:py-2.5 py-1.5 md:px-10 px-4 md:rounded-xl rounded-md flex items-center md:gap-2 gap-1.5 transition ${
              type === "translation"
                ? "bg-gradient-to-r from-[#3AB1F6] to-[#407BFF] text-white font-semibold"
                : "text-[#0A0A0A]"
            }`}
          >
            <ReactSVG
              src={translationIcon}
              beforeInjection={(svg) => svg.setAttribute("class", "w-4 h-auto")}
            />
            Translation
          </button>
        </div>
      </div>

      <div className="flex lg:flex-row flex-col items-stretch gap-8 lg:h-[60vh] h-max lg:min-h-max min-h-max mt-6 max-w-screen-2xl w-full mx-auto">
        {/* Left */}
        <div className="lg:flex-[2] bg-white lg:h-full max-h-[70vh] min-h-[40vh] md:p-6 p-3 flex flex-col rounded-3xl border border-[#3AB1F64D]">
          <div className="flex justify-end shrink-0">
            {wordCount} {wordCount === 1 ? "word" : "words"}
          </div>

             <div className="flex-grow overflow-y-auto">
            {textOut?.length > 0 || refineText?.length > 0 ? (
              <div className="text-[#000] xl:text-xl lg:text-lg md:text-base text-sm font-medium font-inter whitespace-pre-wrap">
                {refineText?.length > 0 ? refineText : textOut}
              </div>
            ) : (
              <div className="text-[#C8C8C8] md:text-xl sm:text-base text-sm font-medium font-inter leading-none flex items-center gap-2">
                <ReactSVG
                  src={voiceStar}
                  beforeInjection={(svg) => {
                    svg.setAttribute("class", "md:w-8 w-6 h-auto");
                  }}
                />
                {type === "transcription"
                  ? recording
                    ? "ZeroTyping AI is listening..."
                    : "Try! ZeroTyping AI Transcription..."
                  : recording
                  ? "ZeroTyping AI is listening..."
                  : "Try! ZeroTyping AI Translation..."}
              </div>
            )}

            {errorMsg ? (
              <div className="mt-3 text-sm text-red-600 font-inter">
                {errorMsg}
              </div>
            ) : null}
          </div>

          {/* Start / Stop */}
          <div className="shrink-0 flex justify-center items-center">
            {!recording ? (
 !(loading || finalizing) ? (
                  <div className="p-4 pb-3">
                  <button onClick={startUnified} className="relative group">
                    <div className="w-[49px] h-[49px] rounded-full flex items-center justify-center">
                      <ReactSVG
                        src={startRecordingIcon}
                        beforeInjection={(svg) => svg.setAttribute("class", "w-full h-full")}
                      />
                    </div>
                    <span
                      className="absolute bottom-full left-1/2 -translate-x-1/2 mb-4 
                      whitespace-nowrap bg-black capitalize text-white text-xs px-3 py-1.5 rounded-full 
                      opacity-0 group-hover:opacity-100 transition-opacity duration-200 
                      z-10 shadow-lg"
                    >
                      {textOut || refineText || errorMsg
                        ? "tap to retry"
                        : "tap icon to start recording!"}
                      <TbTriangleInvertedFilled className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-black" />
                    </span>
                  </button>
                </div>
              ) : (
                <DemoLoader />
              )
            ) : (
              <div className="relative group">
                <Lottie
                  onClick={stopUnified}
                  animationData={iconAnimation}
                  loop
                  style={{ height: 81, width: 81, cursor: "pointer" }}
                />
                <span
                  className="absolute bottom-full left-1/2 -translate-x-1/2 mb-0 
                  whitespace-nowrap bg-black capitalize text-white text-xs px-3 py-1.5 rounded-full 
                  opacity-0 group-hover:opacity-100 transition-opacity duration-200 
                  z-10 shadow-lg"
                >
                  tap to stop recording
                  <TbTriangleInvertedFilled className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-black" />
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Right */}
        <div className="lg:flex-[1] bg-white lg:h-full h-max flex flex-col gap-3 md:p-6 p-3 rounded-3xl border border-[#3AB1F64D]">
          <div className="shrink-0 flex items-center gap-3 md:text-[1.75rem] text-xl font-semibold font-inter">
            <span className="bg-gradient-to-b from-[#3AB1F6] to-[#407BFF] text-white p-1 rounded-lg">
              <IoMdSettings className="md:size-6 size-4" />
            </span>
            Settings
          </div>

          <div className="w-full grow" ref={dropdownRef}>
            <span className="md:text-xl text-base font-inter font-medium">Prompts</span>

            <div className="relative lg:flex flex-col hidden">
              <button
                onClick={() => setIsOpen(!isOpen)}
                style={{ "--bgs": selected?.bgColor }}
                className="flex justify-between items-center p-3 my-2 md:text-base text-sm bg-[#F1FAFF] rounded-lg font-inter w-full text-[#363636]"
              >
                <span className="flex items-center gap-2">
                  <span className="p-2 rounded-md transition bg-[var(--bgs)]">
                    <ReactSVG
                      src={selected.icon}
                      beforeInjection={(svg) => svg.setAttribute("class", "w-3 h-3")}
                    />
                  </span>
                  {selected.label}
                </span>
                <IoIosArrowDown
                  className={`transition-transform duration-300 ${
                    isOpen ? "md:rotate-180 rotate-0" : "md:rotate-0 rotate-180"
                  }`}
                />
              </button>

              <ul
                className={`absolute w-full rounded-lg max-h-36 top-16 shadow-md overflow-y-auto custom-scrollbar bg-[#F1FAFF] transform transition-all duration-500 origin-top z-50 ${
                  isOpen ? "scale-y-100 opacity-100" : "scale-y-0 opacity-0 pointer-events-none"
                }`}
              >
                {options.map((opt, idx) => (
                  <li
                    key={idx}
                    onClick={() => {
                      setSelected(opt);
                      setIsOpen(false);
                    }}
                    style={{ "--bg": opt?.bgColor }}
                    className="group flex items-center gap-2 p-3 cursor-pointer hover:bg-[#3AB1F6] hover:text-white text-base font-inter transition"
                  >
                    <span className="p-2 rounded-md transition bg-[var(--bg)] group-hover:bg-white">
                      <ReactSVG
                        src={opt.icon}
                        beforeInjection={(svg) => svg.setAttribute("class", "w-3 h-3")}
                      />
                    </span>
                    {opt.label}
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative flex flex-col lg:hidden">
              <button
                onClick={() => setIsOpen(!isOpen)}
                style={{ "--bgs": selected?.bgColor }}
                className="flex justify-between items-center p-3 my-2 md:text-base text-sm bg-[#F1FAFF] rounded-lg font-inter w-full text-[#363636]"
              >
                <span className="flex items-center gap-2">
                  <span className="p-2 rounded-md transition bg-[var(--bgs)]">
                    <ReactSVG
                      src={selected.icon}
                      beforeInjection={(svg) => svg.setAttribute("class", "w-3 h-3")}
                    />
                  </span>
                  {selected.label}
                </span>
                <IoIosArrowDown
                  className={`transition-transform duration-300 ${
                    isOpen ? "md:rotate-180 rotate-0" : "md:rotate-0 rotate-180"
                  }`}
                />
              </button>

              <ul
                className={`absolute w-full rounded-lg max-h-36 overflow-y-auto bg-[#F1FAFF] transform transition-all duration-500 origin-bottom bottom-full z-50 ${
                  isOpen ? "scale-y-100 opacity-100" : "scale-y-0 opacity-0 pointer-events-none"
                }`}
              >
                {options.map((opt, idx) => (
                  <li
                    key={idx}
                    onClick={() => {
                      setSelected(opt);
                      setIsOpen(false);
                    }}
                    style={{ "--bg": opt?.bgColor }}
                    className="group flex items-center gap-2 p-3 cursor-pointer hover:bg-[#3AB1F6] hover:text-white text-base font-inter transition"
                  >
                    <span className="p-2 rounded-md transition bg-[var(--bg)] group-hover:bg-white">
                      <ReactSVG
                        src={opt.icon}
                        beforeInjection={(svg) => svg.setAttribute("class", "w-3 h-3")}
                      />
                    </span>
                    {opt.label}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {hastext ? (
            <button
              onClick={() =>
                applyPrompt({
                  content: (refineText || textOut || "").trim(),
                  promptType: selected?.label || "Transcribe Only",
                })
              }
              className="w-full shrink-0 bg-[#3AB1F6] p-3 rounded-xl text-xl font-inter font-medium text-white"
            >
              Apply Settings
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default Demo;
